import { useRef, useState } from "react";
import { Loader2, Send } from "lucide-react";
import { http, errorMessage, fieldErrors } from "../../lib/api";
import { Field, TextInput, TextArea, SelectInput, CheckField, Honeypot, PrivacyAck, FormAlert, SuccessPanel } from "./Fields";
import { FileDrop } from "./FileDrop";
import { LangContext } from "../../lib/lang";

const EMPTY = {
  first_name: "", last_name: "", email: "", phone: "", country: "", profession: "", experience: "",
  german_level: "", region: "", availability: "", message: "", privacy_ack: false,
  talent_pool_consent: false, news_consent: false, website: "",
};
const TXT = {
  de: {
    german: ["Keine Kenntnisse", "A1", "A2", "B1", "B2", "C1", "C2", "Muttersprache", "Weiß ich nicht genau"],
    regions: ["Ganz Deutschland", "Norddeutschland", "Ostdeutschland", "Westdeutschland", "Süddeutschland", "Mitte Deutschlands", "Bestimmte Stadt (bitte in der Nachricht nennen)"],
    choose: "Bitte wählen",
    l: { first_name: "Vorname", last_name: "Nachname", email: "E-Mail", phone: "Telefonnummer", country: "Aktuelles Wohnsitzland", profession: "Beruf", experience: "Berufserfahrung", german_level: "Deutschkenntnisse", region: "Gewünschte Arbeitsregion", availability: "Früheste Verfügbarkeit", message: "Nachricht" },
    ph: { profession: "z. B. Elektriker", experience: "Wie viele Jahre, in welchen Tätigkeiten, welche Abschlüsse oder Führerscheine?", availability: "z. B. ab März 2027", message: "Was möchten Sie uns noch mitteilen?" },
    phoneHint: "Mit Ländervorwahl, z. B. +40 …",
    e: { first_name: "Bitte geben Sie Ihren Vornamen an.", last_name: "Bitte geben Sie Ihren Nachnamen an.", email: "Bitte geben Sie eine gültige E-Mail-Adresse an.", country: "Bitte geben Sie Ihr aktuelles Wohnsitzland an.", profession: "Bitte geben Sie Ihren Beruf an.", privacy_ack: "Bitte bestätigen Sie, dass Sie die Datenschutzhinweise zur Kenntnis genommen haben." },
    check: "Bitte prüfen Sie die markierten Angaben.",
    talent: "Freiwillig: Ich willige ein, dass PACTORA meine Angaben und Unterlagen bis zu 24 Monate in einem Talentpool speichert, um mich auch später für passende Stellen zu kontaktieren. Widerruf jederzeit möglich.",
    news: "Freiwillig: Ich möchte gelegentlich per E-Mail über neue Stellenangebote informiert werden. Widerruf jederzeit möglich.",
    note: "Pflichtfelder sind mit * gekennzeichnet. Die Registrierung ist kostenlos.",
    sending: "Wird gesendet …", submit: "Registrierung senden",
    okTitle: "Vielen Dank für Ihre Registrierung!",
    okFiles: (n) => (n ? ` und ${n} Datei${n > 1 ? "en" : ""}` : ""),
    ok1: (f) => `Wir haben Ihre Angaben${f} sicher erhalten.`,
    ok2: "Unser Team prüft Ihr Profil und meldet sich per E-Mail, sobald wir Fragen haben oder ein passender Arbeitgeber in Frage kommt. Bitte haben Sie Verständnis, dass dies je nach Beruf unterschiedlich lange dauern kann.",
  },
  en: {
    german: ["No knowledge", "A1", "A2", "B1", "B2", "C1", "C2", "Native speaker", "Not sure"],
    regions: ["Anywhere in Germany", "Northern Germany", "Eastern Germany", "Western Germany", "Southern Germany", "Central Germany", "Specific city (please mention in your message)"],
    choose: "Please choose",
    l: { first_name: "First name", last_name: "Last name", email: "Email", phone: "Phone number", country: "Current country of residence", profession: "Profession", experience: "Work experience", german_level: "German language skills", region: "Preferred region in Germany", availability: "Earliest availability", message: "Message" },
    ph: { profession: "e.g. electrician", experience: "How many years, which tasks, which qualifications or driving licences?", availability: "e.g. from March 2027", message: "Is there anything else you would like to tell us?" },
    phoneHint: "With country code, e.g. +40 …",
    e: { first_name: "Please enter your first name.", last_name: "Please enter your last name.", email: "Please enter a valid email address.", country: "Please enter your current country of residence.", profession: "Please enter your profession.", privacy_ack: "Please confirm that you have read the privacy information." },
    check: "Please check the highlighted fields.",
    talent: "Optional: I agree that PACTORA may store my details and documents in a talent pool for up to 24 months in order to contact me about suitable jobs later. I can withdraw this consent at any time.",
    news: "Optional: I would like to receive occasional emails about new job opportunities. I can withdraw this consent at any time.",
    note: "Required fields are marked with *. Registration is free of charge.",
    sending: "Sending …", submit: "Send registration",
    okTitle: "Thank you for registering!",
    okFiles: (n) => (n ? ` and ${n} file${n > 1 ? "s" : ""}` : ""),
    ok1: (f) => `We have securely received your details${f}.`,
    ok2: "Our team will review your profile and contact you by email if we have questions or if a suitable employer is found. Please understand that this can take different amounts of time depending on the profession.",
  },
};

export const ApplicantForm = ({ lang = "de" }) => {
  const t = TXT[lang];
  const [v, setV] = useState(EMPTY);
  const [files, setFiles] = useState([]);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState({ state: "idle" });
  const started = useRef(Date.now());
  const set = (k, val) => { setV((p) => ({ ...p, [k]: val })); setErrors((e) => ({ ...e, [k]: undefined })); };

  const clientCheck = () => {
    const e = {};
    if (!v.first_name.trim()) e.first_name = t.e.first_name;
    if (!v.last_name.trim()) e.last_name = t.e.last_name;
    if (!/^\S+@\S+\.\S+$/.test(v.email.trim())) e.email = t.e.email;
    if (!v.country.trim()) e.country = t.e.country;
    if (!v.profession.trim()) e.profession = t.e.profession;
    if (!v.privacy_ack) e.privacy_ack = t.e.privacy_ack;
    if (errors.files) e.files = errors.files;
    return e;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    const e = clientCheck();
    if (Object.keys(e).length) {
      setErrors(e);
      setStatus({ state: "error", msg: t.check });
      document.getElementById(Object.keys(e)[0])?.focus();
      return;
    }
    setStatus({ state: "sending" });
    const fd = new FormData();
    Object.entries(v).forEach(([k, val]) => fd.append(k, typeof val === "boolean" ? String(val) : val));
    fd.append("started_at", String(started.current));
    fd.append("lang", lang);
    files.forEach((f) => fd.append("files", f));
    try {
      const { data } = await http.post("/forms/applicant", fd, { timeout: 120000 });
      setStatus({ state: "done", reference: data?.reference ?? "", files: data?.files ?? 0 });
    } catch (err) {
      setErrors(fieldErrors(err));
      setStatus({ state: "error", msg: errorMessage(err, lang) });
    }
  };

  if (status.state === "done") {
    return (
      <LangContext.Provider value={lang}>
      <SuccessPanel testid="applicant-success" title={t.okTitle} reference={status.reference}
        onReset={() => { setV(EMPTY); setFiles([]); started.current = Date.now(); setStatus({ state: "idle" }); }}>
        <p>{t.ok1(t.okFiles(status.files))}</p>
        <p>{t.ok2}</p>
      </SuccessPanel>
      </LangContext.Provider>
    );
  }

  return (
    <LangContext.Provider value={lang}>
    <form id="applicant-form" lang={lang} onSubmit={submit} noValidate className="relative space-y-6" data-testid="applicant-form">
      <Honeypot value={v.website} onChange={set} />
      <div className="grid gap-6 md:grid-cols-2">
        <Field id="first_name" label={t.l.first_name} required error={errors.first_name}><TextInput id="first_name" value={v.first_name} onChange={set} error={errors.first_name} required autoComplete="given-name" /></Field>
        <Field id="last_name" label={t.l.last_name} required error={errors.last_name}><TextInput id="last_name" value={v.last_name} onChange={set} error={errors.last_name} required autoComplete="family-name" /></Field>
        <Field id="email" label={t.l.email} required error={errors.email}><TextInput id="email" type="email" value={v.email} onChange={set} error={errors.email} required autoComplete="email" /></Field>
        <Field id="phone" label={t.l.phone} error={errors.phone} hint={t.phoneHint}><TextInput id="phone" type="tel" value={v.phone} onChange={set} error={errors.phone} autoComplete="tel" /></Field>
        <Field id="country" label={t.l.country} required error={errors.country}><TextInput id="country" value={v.country} onChange={set} error={errors.country} required autoComplete="country-name" /></Field>
        <Field id="profession" label={t.l.profession} required error={errors.profession}><TextInput id="profession" value={v.profession} onChange={set} error={errors.profession} required placeholder={t.ph.profession} /></Field>
        <Field id="experience" label={t.l.experience} error={errors.experience} className="md:col-span-2"><TextArea id="experience" rows={3} value={v.experience} onChange={set} error={errors.experience} placeholder={t.ph.experience} /></Field>
        <Field id="german_level" label={t.l.german_level} error={errors.german_level}><SelectInput id="german_level" value={v.german_level} onChange={set} error={errors.german_level} options={t.german} placeholder={t.choose} /></Field>
        <Field id="region" label={t.l.region} error={errors.region}><SelectInput id="region" value={v.region} onChange={set} error={errors.region} options={t.regions} placeholder={t.choose} /></Field>
        <Field id="availability" label={t.l.availability} error={errors.availability} className="md:col-span-2"><TextInput id="availability" value={v.availability} onChange={set} error={errors.availability} placeholder={t.ph.availability} /></Field>
        <Field id="message" label={t.l.message} error={errors.message} className="md:col-span-2"><TextArea id="message" value={v.message} onChange={set} error={errors.message} placeholder={t.ph.message} /></Field>
        <div className="md:col-span-2">
          <FileDrop files={files} setFiles={setFiles} error={errors.files} setError={(msg) => setErrors((e) => ({ ...e, files: msg }))} />
        </div>
      </div>
      <div className="space-y-4 rounded-2xl bg-[color:var(--mist)] p-5 md:p-6">
        <PrivacyAck checked={v.privacy_ack} onChange={set} error={errors.privacy_ack} />
        <CheckField id="talent_pool_consent" checked={v.talent_pool_consent} onChange={set}>
          {t.talent}
        </CheckField>
        <CheckField id="news_consent" checked={v.news_consent} onChange={set}>
          {t.news}
        </CheckField>
      </div>
      {status.state === "error" && <FormAlert type="error">{status.msg}</FormAlert>}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs text-slate-500">{t.note}</p>
        <button type="submit" disabled={status.state === "sending"} data-testid="applicant-submit-button"
          className="inline-flex items-center justify-center gap-2 rounded-full bg-[color:var(--navy)] px-8 py-4 font-semibold text-white transition-[background-color,transform] hover:bg-[color:var(--navy-deep)] active:scale-[0.97] disabled:opacity-60">
          {status.state === "sending" ? <><Loader2 size={18} className="animate-spin" /> {t.sending}</> : <>{t.submit} <Send size={17} /></>}
        </button>
      </div>
    </form>
    </LangContext.Provider>
  );
};
